import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Save, Wallet, ShieldCheck, Warehouse, Boxes, Store, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, fmtDate, fmtDateTime, fmtErr } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";
import { AdminWalletAdjust } from "../../components/AdminWalletAdjust";

const inputCls = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-shipany-navy focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={testid}>
      <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">{Icon && <Icon className="h-4 w-4 text-shipany-blue" />} {title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adjusting, setAdjusting] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/customers/${id}/detail`).then(({ data }) => {
      setData(data);
      const c = data.customer;
      setForm({ name: c.name || "", email: c.email || "", phone: c.phone || "", company: c.company || "", status: c.status || "active" });
    }).catch((e) => { toast.error(fmtErr(e)); setData({}); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/customers/${id}`, form);
      toast.success(`Saved — updated: ${data.updated.join(", ")}`);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  if (!data || !form) return <FullScreenLoader label="Loading customer" />;
  const c = data.customer || {};
  const w = data.wallet || {};

  return (
    <div className="space-y-5" data-testid="admin-customer-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/customers" data-testid="back-to-customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-shipany-blue hover:underline"><ArrowLeft className="h-4 w-4" /> All customers</Link>
          <h1 className="mt-1 font-display text-2xl font-bold text-shipany-navy" data-testid="customer-detail-name">{c.name}</h1>
          <p className="text-sm text-slate-500">{c.email} · joined {fmtDate(c.created_at)} · via {c.auth_provider || "jwt"}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`} data-testid="customer-detail-status">{c.status}</span>
          <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${c.kyc_status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="customer-detail-kyc">KYC {c.kyc_status || "not_submitted"}</span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Edit Profile" icon={Save} testid="customer-edit-card">
          <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Full name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-edit-name" className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Company</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="cust-edit-company" className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-edit-email" className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-edit-phone" className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Account status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="cust-edit-status" className={inputCls}>
                <option value="active">Active</option><option value="suspended">Suspended</option>
              </select></div>
            <div className="flex items-end">
              <button disabled={saving} data-testid="cust-save-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-shipany-blue py-2.5 text-sm font-bold text-white disabled:opacity-60">
                {saving ? "Saving…" : <><Save className="h-4 w-4" /> Save changes</>}
              </button>
            </div>
          </form>
        </Card>

        <Card title="Wallet" icon={Wallet} testid="customer-wallet-card">
          <div className="flex items-center justify-between">
            <div><p className="text-xs font-semibold text-slate-400">Balance</p><p className="mono text-2xl font-bold text-emerald-600" data-testid="customer-wallet-balance">{inr(w.balance ?? 0)}</p></div>
            <button onClick={() => setAdjusting(true)} data-testid="customer-add-balance-btn" className="rounded-xl bg-shipany-blue px-4 py-2 text-xs font-bold text-white">Add / Deduct</button>
          </div>
          <div className="mt-4 space-y-1.5">
            {(data.transactions || []).slice(0, 5).map((t) => (
              <div key={t.txn_id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs" data-testid={`cust-txn-${t.txn_id}`}>
                <span className="text-slate-500">{t.note || t.category}</span>
                <span className={`mono font-bold ${t.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>{t.type === "credit" ? "+" : "−"}{inr(t.amount)}</span>
              </div>
            ))}
            {!(data.transactions || []).length && <p className="py-2 text-center text-xs text-slate-400">No wallet transactions</p>}
          </div>
        </Card>

        <Card title="KYC" icon={ShieldCheck} testid="customer-kyc-card">
          {data.kyc ? (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">Status</dt><dd className="font-bold capitalize">{data.kyc.status}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">PAN</dt><dd className="mono font-semibold">{data.kyc.pan || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">GST</dt><dd className="mono font-semibold">{data.kyc.gst || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Address</dt><dd className="text-right font-semibold">{[data.kyc.address?.line1, data.kyc.address?.city, data.kyc.address?.state, data.kyc.address?.pincode].filter(Boolean).join(", ") || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Submitted</dt><dd className="font-semibold">{fmtDateTime(data.kyc.submitted_at)}</dd></div>
              <Link to="/admin/kyc" data-testid="customer-kyc-review-link" className="inline-block pt-1 text-xs font-bold text-shipany-blue hover:underline">Review documents in KYC Center →</Link>
            </dl>
          ) : <p className="text-xs text-slate-400" data-testid="kyc-not-submitted">No KYC submission yet.</p>}
        </Card>

        <Card title={`Warehouses (${(data.warehouses || []).length})`} icon={Warehouse} testid="customer-warehouses-card">
          <div className="space-y-2">
            {(data.warehouses || []).map((wh) => (
              <div key={wh.warehouse_id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs" data-testid={`cust-wh-${wh.warehouse_id}`}>
                <span className="font-semibold text-shipany-navy">{wh.serial ? `WH-${wh.serial} · ` : ""}{wh.name}</span>
                <span className="text-slate-500">{wh.city} {wh.pincode}</span>
              </div>
            ))}
            {!(data.warehouses || []).length && <p className="py-2 text-center text-xs text-slate-400">No warehouses</p>}
          </div>
        </Card>

        {data.franchise && (
          <Card title="Franchise Link" icon={Store} testid="customer-franchise-card">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">Franchise</dt><dd className="font-semibold">{data.franchise.name}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Code</dt><dd className="mono font-semibold">{data.franchise.franchise_code}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Franchise status</dt><dd className="font-semibold capitalize">{data.franchise.status}</dd></div>
            </dl>
          </Card>
        )}

        <Card title={`Shipments (${(data.shipments || []).length ? "recent" : "0"})`} icon={Boxes} testid="customer-shipments-card">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {Object.entries(data.shipment_status_counts || {}).map(([s, n]) => (
              <span key={s} className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-600">{s}: {n}</span>
            ))}
            <span className="rounded-full bg-shipany-blue/10 px-2.5 py-1 text-[10px] font-bold text-shipany-blue">INTL: {data.international_count}</span>
          </div>
          <div className="space-y-2">
            {(data.shipments || []).map((s) => (
              <div key={s.shipment_id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs" data-testid={`cust-ship-${s.shipment_id}`}>
                <span className="mono font-semibold text-shipany-navy">{s.awb || s.order_number}</span>
                <span className="text-slate-500">{s.courier_name} → {s.destination_city}</span>
                <span className="mono font-bold">{inr(s.price || 0)}</span>
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold">{s.status}</span>
              </div>
            ))}
            {!(data.shipments || []).length && <p className="py-2 text-center text-xs text-slate-400">No shipments yet</p>}
          </div>
        </Card>
      </div>

      {adjusting && <AdminWalletAdjust target={{ user_id: c.user_id, email: c.email, name: c.name, balance: w.balance ?? 0 }} onClose={() => setAdjusting(false)} onDone={() => { setAdjusting(false); load(); }} />}
    </div>
  );
}
